import type {Collection, Node, Selection} from '@react-types/shared';
import type {Key} from 'react';
import {useRef} from 'react';

import {useUpdateEffect} from '../utils';
import {announce} from '../live-announcer';
import {useLocalizedStringFormatter} from '../i18n';
import type {SelectionManager} from '../../@react-stately/selection';
import intlMessages from './intl';

export interface GridSelectionAnnouncementProps {
  /**
   * A function that returns the text that should be announced by assistive technology when a row is added or removed from selection.
   * @default (key) => state.collection.getItem(key)?.textValue
   */
  getRowText?: (key: Key) => string;
}

interface GridSelectionState<T> {
  /** A collection of items in the grid. */
  collection: Collection<Node<T>>;
  /** A set of items that are disabled. */
  disabledKeys: Set<Key>;
  /** A selection manager to read and update multiple selection state. */
  selectionManager: SelectionManager;
}

function diffSelection(a: Selection, b: Selection): Set<Key> {
  const res = new Set<Key>();
  if (a === 'all' || b === 'all') {
    return res;
  }

  const keys = Array.from(a.keys());
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (!b.has(key)) {
      res.add(key);
    }
  }

  return res;
}

export function useGridSelectionAnnouncement<T>(
  props: GridSelectionAnnouncementProps,
  state: GridSelectionState<T>,
) {
  const {
    getRowText = (key) =>
      state.collection.getTextValue?.(key) ??
      state.collection.getItem(key)?.textValue,
  } = props;
  const stringFormatter = useLocalizedStringFormatter(intlMessages);

  // Many screen readers do not announce when items in a grid are selected/deselected.
  // We do this using an ARIA live region.
  const selection = state.selectionManager.rawSelection;
  const lastSelection = useRef(selection);
  useUpdateEffect(() => {
    if (!state.selectionManager.isFocused) {
      lastSelection.current = selection;

      return;
    }

    const addedKeys = diffSelection(selection, lastSelection.current);
    const removedKeys = diffSelection(lastSelection.current, selection);

    // If adding or removing a single row from the selection, announce the name of that item.
    const isReplace = state.selectionManager.selectionBehavior === 'replace';
    const messages = [];

    if (state.selectionManager.selectedKeys.size === 1 && isReplace) {
      if (
        state.collection.getItem(
          state.selectionManager.selectedKeys.keys().next().value,
        )
      ) {
        const currentSelectionText = getRowText(
          state.selectionManager.selectedKeys.keys().next().value,
        );
        if (currentSelectionText) {
          messages.push(
            stringFormatter.format('selectedItem', {
              item: currentSelectionText,
            }),
          );
        }
      }
    } else if (addedKeys.size === 1 && removedKeys.size === 0) {
      const addedText = getRowText(addedKeys.keys().next().value);
      if (addedText) {
        messages.push(
          stringFormatter.format('selectedItem', {item: addedText}),
        );
      }
    } else if (removedKeys.size === 1 && addedKeys.size === 0) {
      if (state.collection.getItem(removedKeys.keys().next().value)) {
        const removedText = getRowText(removedKeys.keys().next().value);
        if (removedText) {
          messages.push(
            stringFormatter.format('deselectedItem', {item: removedText}),
          );
        }
      }
    }

    // Announce how many items are selected, except when selecting the first item.
    if (state.selectionManager.selectionMode === 'multiple') {
      if (
        messages.length === 0 ||
        selection === 'all' ||
        selection.size > 1 ||
        lastSelection.current === 'all' ||
        lastSelection.current?.size > 1
      ) {
        messages.push(
          selection === 'all'
            ? stringFormatter.format('selectedAll')
            : stringFormatter.format('selectedCount', {count: selection.size}),
        );
      }
    }

    if (messages.length > 0) {
      announce(messages.join(' '));
    }

    lastSelection.current = selection;
  }, [selection]);
}
